import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { confirm, haptic } from '../lib/telegram';
import EntryRow from '../components/EntryRow';
import type { Entry, Currency, Kind } from '../lib/types';
import type { Screen } from '../App';

interface Props {
  onBack: () => void;
  navigate: (s: Screen) => void;
}

export default function History({ onBack, navigate }: Props) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [filterCurrency, setFilterCurrency] = useState<'' | Currency>('');
  const [filterKind, setFilterKind] = useState<'' | Kind>('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load(reset: boolean) {
    setErr(null);
    setBusy(true);
    try {
      const res = await api.list({
        limit: 50,
        cursor: reset ? undefined : (cursor ?? undefined),
        currency: filterCurrency || undefined,
        kind: filterKind || undefined,
        q: search.trim() || undefined,
      });
      setEntries(reset ? res.entries : [...entries, ...res.entries]);
      setCursor(res.next_cursor);
      setHasMore(res.next_cursor !== null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load');
    } finally {
      setBusy(false);
    }
  }

  // Filter changes load immediately; only the typed search debounces.
  useEffect(() => { load(true); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [filterCurrency, filterKind]);
  const searchSkipInit = useRef(true);
  useEffect(() => {
    if (searchSkipInit.current) { searchSkipInit.current = false; return; }
    const t = setTimeout(() => load(true), 300);
    return () => clearTimeout(t);
    /* eslint-disable-line react-hooks/exhaustive-deps */
  }, [search]);

  async function handleTap(entry: Entry) {
    // Exchange entries can only be deleted (they need to update both halves).
    if (entry.kind === 'exchange') {
      if (!(await confirm('Delete this exchange? Both sides will be removed.'))) return;
      try {
        await api.remove(entry.id);
        haptic('success');
        load(true);
      } catch (e) {
        haptic('error');
        setErr(e instanceof Error ? e.message : 'delete failed');
      }
      return;
    }
    navigate({ name: 'edit', entry });
  }

  return (
    <>
      <div className="header">
        <button className="back" onClick={onBack}>← Back</button>
        <h1>History</h1>
      </div>
      {err && <div className="error">{err}</div>}

      <div className="card">
        <input
          className="input"
          type="search"
          placeholder="🔍 Search notes & categories"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <label className="label">Currency</label>
        <div className="segmented">
          <button type="button" className={filterCurrency === '' ? 'active' : ''} onClick={() => setFilterCurrency('')}>All</button>
          <button type="button" className={filterCurrency === 'USD' ? 'active' : ''} onClick={() => setFilterCurrency('USD')}>USD</button>
          <button type="button" className={filterCurrency === 'SYP' ? 'active' : ''} onClick={() => setFilterCurrency('SYP')}>SYP</button>
        </div>
        <label className="label">Type</label>
        <div className="segmented">
          <button type="button" className={filterKind === '' ? 'active' : ''} onClick={() => setFilterKind('')}>All</button>
          <button type="button" className={filterKind === 'income' ? 'active' : ''} onClick={() => setFilterKind('income')}>Income</button>
          <button type="button" className={filterKind === 'expense' ? 'active' : ''} onClick={() => setFilterKind('expense')}>Expense</button>
          <button type="button" className={filterKind === 'exchange' ? 'active' : ''} onClick={() => setFilterKind('exchange')}>Exchange</button>
        </div>
      </div>

      <div className="card">
        {entries.length === 0 ? (
          <div className="empty">{busy ? 'Loading…' : 'No matching entries.'}</div>
        ) : (
          entries.map((e) => <EntryRow key={e.id} entry={e} onTap={handleTap} />)
        )}
      </div>

      {hasMore && (
        <button className="btn btn-secondary" onClick={() => load(false)} disabled={busy}>
          {busy ? 'Loading…' : 'Load more'}
        </button>
      )}
    </>
  );
}
